// Pure helpers for the Android provisioning QR (Kitchen QR Provisioning, spec 2026-11-01 D-2 / section 5).
// No React, no network, no Node globals: this runs in the browser and in Jest (jsdom).

const P = 'android.app.extra.PROVISIONING_';

export const AGENT_ADMIN_COMPONENT = 'co.thisway.agent/co.thisway.agent.service.AgentDeviceAdminReceiver';
export const MIN_QR_AGENT_VERSION_CODE = 8;

// The agent's FleetHosts list (test and prod API origins). QR-2: no fallback to the page origin.
const FLEET_API_ORIGINS = ['https://api-test.thisway.co', 'https://api.thisway.co'];

const UPLOAD_SENTENCE = 'Upload agent 0.2.1 or newer and mark it Current to get a setup QR.';
const SECURITY_TYPES = ['WPA', 'WEP', 'NONE'];

// Standard base64 of the 32 bytes with + -> -, / -> _ and the padding stripped.
// Accepts upper or lower case and surrounding whitespace; throws on anything that is not 64 hex characters.
export function hexToBase64Url(hex) {
  const text = typeof hex === 'string' ? hex.trim() : '';
  if (text.length % 2 !== 0) throw new Error('sha256 has an odd number of characters');
  if (!/^[0-9a-fA-F]*$/.test(text)) throw new Error('sha256 is not hexadecimal');
  if (text.length !== 64) throw new Error('sha256 must be 64 hexadecimal characters');

  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i += 1) {
    bytes[i] = parseInt(text.slice(i * 2, i * 2 + 2), 16);
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// The origin of the console's API base, or null unless it is exactly one of the two fleet hosts.
export function fleetApiOrigin(baseURL) {
  if (typeof baseURL !== 'string' || baseURL.trim() === '') return null;
  let origin;
  try {
    origin = new URL(baseURL.trim()).origin;
  } catch (e) {
    return null;
  }
  return FLEET_API_ORIGINS.includes(origin) ? origin : null;
}

export function buildProvisioningPayload({ apkUrl, sha256Hex, apiBase, code, wifi }) {
  const payload = {
    [`${P}DEVICE_ADMIN_COMPONENT_NAME`]: AGENT_ADMIN_COMPONENT,
    [`${P}DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION`]: String(apkUrl == null ? '' : apkUrl).trim(),
    [`${P}DEVICE_ADMIN_PACKAGE_CHECKSUM`]: hexToBase64Url(sha256Hex),
    [`${P}LEAVE_ALL_SYSTEM_APPS_ENABLED`]: true,
  };

  // Wi-Fi keys only for a non-blank SSID, and (for WPA/WEP) only with a password: a protected
  // network without one cannot join, so the QR carries no Wi-Fi at all rather than a half network.
  const ssid = wifi && typeof wifi.ssid === 'string' ? wifi.ssid.trim() : '';
  if (ssid) {
    const security = SECURITY_TYPES.includes(wifi.security) ? wifi.security : 'WPA';
    if (security === 'NONE' || wifi.password) {
      payload[`${P}WIFI_SSID`] = ssid;
      if (security !== 'NONE') payload[`${P}WIFI_PASSWORD`] = wifi.password;
      payload[`${P}WIFI_SECURITY_TYPE`] = security;
      if (wifi.hidden === true) payload[`${P}WIFI_HIDDEN`] = true;
    }
  }

  payload[`${P}ADMIN_EXTRAS_BUNDLE`] = {
    'co.thisway.agent.api': apiBase,
    'co.thisway.agent.code': String(code == null ? '' : code)
      .replace(/[-\s]/g, '')
      .toUpperCase(),
  };
  return payload;
}

// { ok: true } or { ok: false, reason }. reason is null while the releases request is loading (render nothing).
export function canShowSetupQr({ status, release, apiOrigin }) {
  if (status === 'loading') return { ok: false, reason: null };
  if (status === 'failed') return { ok: false, reason: 'Could not load the releases.' };
  if (status !== 'ready' || !release || !(Number(release.versioncode) >= MIN_QR_AGENT_VERSION_CODE)) {
    return { ok: false, reason: UPLOAD_SENTENCE };
  }
  if (typeof release.fileurl !== 'string' || release.fileurl.trim() === '') {
    return { ok: false, reason: 'The Current agent release has no download link.' };
  }
  try {
    hexToBase64Url(release.sha256);
  } catch (e) {
    return { ok: false, reason: 'The Current agent release has no valid SHA-256 checksum.' };
  }
  if (!apiOrigin) return { ok: false, reason: 'This console does not talk to a fleet API host.' };
  return { ok: true };
}

const slug = (value, fallback) => {
  const text = String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
  return text || fallback;
};

// thisway-setup-<role>-<storename-slug>.png. Takes no code on purpose: the file name must never carry it.
export function setupQrFileName(role, storeName) {
  return `thisway-setup-${slug(role, 'device')}-${slug(storeName, 'store')}.png`;
}
